import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, FileSpreadsheet, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { TvcAdminPanel } from '@/components/pricing/TvcAdminPanel';
import { AxeFeesCard } from '@/components/pricing/AxeFeesCard';

const STATUS_CLS = { draft: 'bg-amber-100 text-amber-800', approved: 'bg-green-100 text-green-800', superseded: 'bg-zinc-100 text-zinc-600' };

// Admin: lender rate sheets (versioned rule sets), workbook inspection, broker compensation.
export default function Pricing() {
  const [ruleSets, setRuleSets] = useState([]);
  const [lenders, setLenders] = useState([]);
  const [comp, setComp] = useState({ origination_points: 0, processing_fee: 0, max_total_points: '' });
  const [selected, setSelected] = useState(null);
  const [programsJson, setProgramsJson] = useState('');
  const [inspect, setInspect] = useState(null);
  const [newSet, setNewSet] = useState({ lender_id: '', name: '', effective_date: '', source_doc_id: '' });

  const load = async () => {
    const [rs, ls, c] = await Promise.all([api.get('/pricing/rule-sets'), api.get('/lenders'), api.get('/pricing/broker-compensation')]);
    setRuleSets(rs.data.rule_sets); setLenders(ls.data); setComp({ ...c.data, max_total_points: c.data.max_total_points ?? '' });
  };
  useEffect(() => { load().catch(() => toast.error('Failed to load pricing data')); }, []);

  const excelDocs = lenders.flatMap((l) => (l.program_documents || []).filter((d) => d.file_kind === 'excel').map((d) => ({ ...d, lender_id: l.lender_id, lender_name: l.lender_name })));

  const saveComp = async () => {
    try {
      await api.put('/pricing/broker-compensation', { ...comp, max_total_points: comp.max_total_points === '' ? null : Number(comp.max_total_points) });
      toast.success('Broker compensation saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Admin only'); }
  };

  const createSet = async () => {
    if (!newSet.lender_id || !newSet.name) return toast.error('Lender and name are required');
    try {
      const r = await api.post('/pricing/rule-sets', { ...newSet, effective_date: newSet.effective_date || null, source_doc_id: newSet.source_doc_id || null, programs: [] });
      toast.success(`Draft rule set v${r.data.version} created — add programs, then approve`);
      await load(); openSet(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openSet = (rs) => { setSelected(rs); setProgramsJson(JSON.stringify(rs.programs || [], null, 2)); };

  const saveSet = async () => {
    let programs;
    try { programs = JSON.parse(programsJson); } catch { return toast.error('Programs JSON is invalid'); }
    try {
      const r = await api.put(`/pricing/rule-sets/${selected.rule_set_id}`, { programs });
      toast.success('Draft saved'); setSelected(r.data); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const approveSet = async (rs) => {
    try {
      await api.post(`/pricing/rule-sets/${rs.rule_set_id}/approve`);
      toast.success(`Approved v${rs.version} — previous version kept as superseded`); load(); setSelected(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Approval failed'); }
  };

  const inspectDoc = async (d) => {
    try {
      const r = await api.post(`/lenders/${d.lender_id}/documents/${d.doc_id}/inspect-workbook`);
      setInspect({ ...r.data, doc: d });
      toast.success(`${r.data.sheet_count} sheet(s) read`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not read workbook'); }
  };

  return (
    <div className="space-y-6" data-testid="pricing-page">
      <div>
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Pricing Engine</h1>
        <p className="text-muted-foreground">Lender rate sheets are versioned; only approved versions price borrower scenarios. Nothing is estimated.</p>
      </div>

      <AxeFeesCard />
      <TvcAdminPanel />

      <section className="border rounded-xl p-4 space-y-3" data-testid="broker-comp-section">
        <h2 className="font-semibold">Broker compensation (included in preliminary pricing)</h2>
        <p className="text-xs text-amber-800">Applies to non-TVC rate-sheet pricing only. TVC quotes compute AXE compensation from the TVC Sales Tool (earned origination above floor + premium / DBP) — confirm against the TVC broker agreement.</p>
        <div className="grid sm:grid-cols-3 gap-2">
          <div><label className="text-xs text-muted-foreground">Origination points</label><Input type="number" step="0.125" value={comp.origination_points} onChange={(e) => setComp({ ...comp, origination_points: Number(e.target.value) })} data-testid="comp-points" /></div>
          <div><label className="text-xs text-muted-foreground">Processing fee ($)</label><Input type="number" value={comp.processing_fee} onChange={(e) => setComp({ ...comp, processing_fee: Number(e.target.value) })} data-testid="comp-fee" /></div>
          <div><label className="text-xs text-muted-foreground">Max total points (lender + broker)</label><Input type="number" step="0.125" value={comp.max_total_points} onChange={(e) => setComp({ ...comp, max_total_points: e.target.value })} data-testid="comp-max" /></div>
        </div>
        <Button size="sm" onClick={saveComp} data-testid="comp-save-btn">Save compensation</Button>
      </section>

      <section className="border rounded-xl p-4 space-y-3" data-testid="excel-docs-section">
        <h2 className="font-semibold flex items-center gap-2"><FileSpreadsheet className="h-4 w-4" /> Uploaded Excel rate sheets</h2>
        {excelDocs.length === 0 && <p className="text-sm text-muted-foreground">Upload a lender's .xlsx on the Lenders page (Documents) and it will appear here.</p>}
        {excelDocs.map((d) => (
          <div key={d.doc_id} className="flex flex-wrap items-center justify-between gap-2 text-sm border-t pt-2" data-testid={`excel-doc-${d.doc_id}`}>
            <span>{d.lender_name} · {d.original_filename} · {new Date(d.uploaded_at).toLocaleDateString()}{d.workbook_summary ? ` · ${d.workbook_summary.sheet_count} sheet(s)` : ''}</span>
            <Button size="sm" variant="outline" onClick={() => inspectDoc(d)} data-testid={`inspect-${d.doc_id}`}>Inspect workbook</Button>
          </div>
        ))}
        {inspect && (
          <div className="bg-muted rounded-lg p-3 text-xs space-y-2" data-testid="inspect-result">
            <p className="font-medium">{inspect.doc.original_filename}: {inspect.sheet_count} sheet(s){inspect.formula_engine_error ? ` · formula engine: ${inspect.formula_engine_error}` : ''}</p>
            {inspect.sheets.map((s) => (
              <details key={s.name} open>
                <summary className="cursor-pointer">{s.name} — {s.dimensions}, {s.formula_count} formula(s), {s.named_tables.length} table(s)</summary>
                <div className="overflow-x-auto mt-1"><table className="text-[11px]"><tbody>{s.preview.map((r, i) => <tr key={i}>{r.map((v, j) => <td key={j} className="border px-1 whitespace-nowrap">{v}</td>)}</tr>)}</tbody></table></div>
                {s.formula_samples.length > 0 && <ul className="mt-1 list-disc list-inside">{s.formula_samples.slice(0, 15).map((f) => <li key={f.cell}><code>{f.cell}</code> {f.formula} → <strong>{String(f.cached_value ?? 'n/a')}</strong></li>)}</ul>}
              </details>
            ))}
          </div>
        )}
      </section>

      <section className="border rounded-xl p-4 space-y-3" data-testid="rule-sets-section">
        <h2 className="font-semibold">Rate sheet rule sets</h2>
        <div className="grid sm:grid-cols-5 gap-2 items-end">
          <div className="sm:col-span-2">
            <label className="text-xs text-muted-foreground">Lender</label>
            <Select value={newSet.lender_id} onValueChange={(v) => setNewSet({ ...newSet, lender_id: v })}>
              <SelectTrigger data-testid="new-set-lender"><SelectValue placeholder="Select lender" /></SelectTrigger>
              <SelectContent>{lenders.map((l) => <SelectItem key={l.lender_id} value={l.lender_id}>{l.lender_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className="text-xs text-muted-foreground">Rule set name</label><Input value={newSet.name} onChange={(e) => setNewSet({ ...newSet, name: e.target.value })} placeholder="e.g. Bridge Rate Sheet" data-testid="new-set-name" /></div>
          <div><label className="text-xs text-muted-foreground">Effective date</label><Input type="date" value={newSet.effective_date} onChange={(e) => setNewSet({ ...newSet, effective_date: e.target.value })} data-testid="new-set-date" /></div>
          <Button size="sm" onClick={createSet} data-testid="new-set-create-btn"><Plus className="h-4 w-4 mr-1" /> New draft version</Button>
        </div>
        <div className="divide-y">
          {ruleSets.map((rs) => (
            <div key={rs.rule_set_id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm" data-testid={`rule-set-${rs.rule_set_id}`}>
              <div>
                <span className="font-medium">{rs.lender_name}</span> · {rs.name} · v{rs.version}
                <span className="text-muted-foreground"> · {rs.programs?.length || 0} program(s){rs.effective_date ? ` · effective ${rs.effective_date}` : ''}{rs.approved_at ? ` · approved ${new Date(rs.approved_at).toLocaleDateString()}` : ''}</span>
                <Badge className={`ml-2 ${STATUS_CLS[rs.status]}`}>{rs.status}</Badge>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openSet(rs)} data-testid={`open-set-${rs.rule_set_id}`}>{rs.status === 'draft' ? 'Edit' : 'View'}</Button>
                {rs.status === 'draft' && <Button size="sm" onClick={() => approveSet(rs)} data-testid={`approve-set-${rs.rule_set_id}`}><CheckCircle className="h-4 w-4 mr-1" /> Approve</Button>}
              </div>
            </div>
          ))}
        </div>
        {selected && (
          <div className="space-y-2" data-testid="rule-set-editor">
            <p className="text-sm font-medium">{selected.lender_name} · {selected.name} v{selected.version} ({selected.status})</p>
            <p className="text-xs text-muted-foreground">Programs: eligibility, rate_grid (row_axis/col_axis bands + cells), adjustments, points, fees, term. Leave unknown values out — the engine then reports "needs lender confirmation" instead of guessing.</p>
            <Textarea value={programsJson} onChange={(e) => setProgramsJson(e.target.value)} rows={14} className="font-mono text-xs" disabled={selected.status !== 'draft'} data-testid="rule-set-json" />
            {selected.status === 'draft' && <Button size="sm" onClick={saveSet} data-testid="rule-set-save-btn">Save draft</Button>}
          </div>
        )}
      </section>
    </div>
  );
}
